/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { pluginsAddPluginServer } from "../funcs/pluginsAddPluginServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { PluginServer } from "../models/components/pluginserver.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AddPluginServerRequest,
  AddPluginServerSecurity,
} from "../models/operations/addpluginserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type AddPluginServerMutationVariables = {
  request: AddPluginServerRequest;
  security?: AddPluginServerSecurity | undefined;
  options?: RequestOptions;
};

export type AddPluginServerMutationData = PluginServer;

export type AddPluginServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * addPluginServer plugins
 *
 * @remarks
 * Add an MCP server to a plugin.
 */
export function useAddPluginServerMutation(
  options?: MutationHookOptions<
    AddPluginServerMutationData,
    AddPluginServerMutationError,
    AddPluginServerMutationVariables
  >,
): UseMutationResult<
  AddPluginServerMutationData,
  AddPluginServerMutationError,
  AddPluginServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildAddPluginServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyAddPluginServer(): MutationKey {
  return ["@gram/client", "plugins", "addPluginServer"];
}

export function buildAddPluginServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: AddPluginServerMutationVariables,
  ) => Promise<AddPluginServerMutationData>;
} {
  return {
    mutationKey: mutationKeyAddPluginServer(),
    mutationFn: function addPluginServerMutationFn({
      request,
      security,
      options,
    }): Promise<AddPluginServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(pluginsAddPluginServer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
